import type { ReactNode } from 'react';
import { MethodBadge } from '../common/MethodBadge';
import { StatusBadge } from '../common/StatusBadge';
import { Prompt, Terminal } from './Terminal';

interface Step {
  label: string;
  description: string;
  /** What the step leaves you holding: the URL, the command, the log line. */
  artifact: ReactNode;
}

const STEPS: Step[] = [
  {
    label: '01 create',
    description:
      'Click once and get a project with its own subdomain. Add endpoints with a path, status, headers and body, or start from a template.',
    artifact: (
      <code className="block font-mono text-sm text-primary bg-primary/10 px-3 py-2 rounded-field truncate">
        https://my-app.mockd.sh
      </code>
    ),
  },
  {
    label: '02 send',
    description:
      'Point your app, a webhook provider, or curl at the URL. Every HTTP method is accepted.',
    artifact: (
      <Terminal chrome className="text-xs">
        <span className="whitespace-pre">
          <Prompt />
          {`curl -X POST \\
  https://my-app.mockd.sh/api/webhooks \\
  -d '{"event":"user.signup"}'`}
        </span>
      </Terminal>
    ),
  },
  {
    label: '03 inspect',
    description:
      'The request shows up in your log as it happens, with its headers, body, query and timing.',
    artifact: (
      // base-100, not code-surface: the badges only reach AA on base-100.
      <div className="flex items-center gap-3 px-3 py-2 bg-base-100 border border-base-300 rounded-box">
        <MethodBadge method="POST" />
        <StatusBadge status={200} />
        <span className="flex-1 font-mono text-sm text-base-content truncate">/api/webhooks</span>
        <span className="font-mono text-xs text-base-content/70">14ms</span>
      </div>
    ),
  },
];

export function HowItWorks() {
  return (
    <section className="py-20 bg-base-200 border-t border-base-300">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl tracking-tight">From nothing to a live URL in one click</h2>
        <p className="mt-3 text-lg text-base-content/70 max-w-2xl">
          No signup required. Create a mock endpoint and start capturing requests immediately.
        </p>

        <ol className="mt-12 grid grid-cols-1 lg:grid-cols-3 rounded-box border border-base-300 bg-base-100 divide-y lg:divide-y-0 lg:divide-x divide-base-300">
          {STEPS.map((step) => (
            <li key={step.label} className="p-6 flex flex-col gap-4 min-w-0">
              <h3 className="font-mono text-sm font-medium text-primary">{step.label}</h3>
              <p className="text-base-content/80 leading-relaxed">{step.description}</p>
              <div className="mt-auto">{step.artifact}</div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
